import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKey } from '../../../constants/queryKey';
import { searchService } from 'shared/src/api';
import { isAndroid } from 'shared/src/utils';
import type { SuggestKeywordResponse } from 'shared/src/api/searchService/types';

export const useSuggestKeywords = (query: string) => {
  const queryClient = useQueryClient();

  // Android: SearchHelper 새 인스턴스 mount 시 suggest 캐시 prefix 제거.
  // jotai atom에 남은 이전 검색어 + staleTime 10초 캐시 hit으로 신규 진입 시
  // 이전 검색어의 자동완성이 잠깐 노출되는 race 방지. SearchResult 복귀 시는
  // 인스턴스가 살아있어 mount되지 않아 검색어 복원 흐름 영향 없음.
  useEffect(() => {
    if (isAndroid) {
      queryClient.removeQueries({ queryKey: ['suggestKeywords'] });
    }
  }, [queryClient]);

  const result = useQuery({
    queryKey: queryKey.suggestKeywords(query),
    queryFn: () => searchService.getSuggestKeywords(query),
    enabled: query.trim().length > 0,
    staleTime: 1000 * 10,
    placeholderData: prev => prev,
  });

  // 빈 결과가 아닌 마지막 데이터를 유지 (iOS: fetchSuggestedItems에서 빈 결과 시 state 미갱신)
  const lastNonEmptyData = useRef<SuggestKeywordResponse | undefined>(
    undefined,
  );

  // 검색어가 비면 ref 초기화 (다른 검색어 입력 시 이전 결과 노출 방지)
  if (query.trim().length === 0) {
    lastNonEmptyData.current = undefined;
  } else if (
    result.data &&
    (result.data.linked_suggesters.length > 0 ||
      result.data.query_suggesters.length > 0)
  ) {
    lastNonEmptyData.current = result.data;
  }

  return { data: lastNonEmptyData.current ?? result.data };
};
